import React, { FC, Fragment, useState } from "react";
import { IMensagem, utils } from "@deltasge/marauders-map";
import sanitizeHtml from "sanitize-html";
import { decode } from "html-entities";

import {
  DotsVertical as MoreIcon,
  Pencil as EditIcon,
  Delete as DeleteIcon,
  TextBoxSearch as DetailIcon,
  Check as PublicarIcon,
  Heart as CurtidaIcon,
  ContentCopy,
} from "mdi-material-ui";

import {
  differenceInMinutes,
  format,
  formatDistance,
  parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { useMobile } from "hooks/useMobile";
import {
  Badge,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAppSelector } from "store";
import { IMensagemCategoria } from "../utilMensagem";
import { snack } from "components/GlobalSnackbar";
import { RowEnviadaAtribuicao } from "./RowEnviadaAtribuicao";

export const Row: FC<{
  item: IMensagem;
  handleEdit: (args: {
    idMensagem: number;
    categoria: IMensagemCategoria;
  }) => void;
  handleDetalhes: (idMensagem: number) => void;
  handleDelete: (idMensagem: number) => void;
  handlePublicar: (idMensagem: number, publicar: boolean) => void;
}> = ({ item, handleEdit, handleDetalhes, handleDelete, handlePublicar }) => {
  const {
    chat: { unreads: messages },
    escola,
  } = useAppSelector((state) => state);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isSm = useMobile("sm");
  const isMd = useMobile("md");

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  let title = item.titulo;
  if (item.texto && !item.titulo) {
    title = decode(
      sanitizeHtml(item.texto, {
        allowedTags: [],
      })
    );
    if (title.length > 200) {
      title = title.substring(0, 100).concat("...");
    }
  }

  const qtdeCurtidas = (item.mensagemCurtidas ?? []).length;
  let curtidas = "Nenhuma curtida";
  if (qtdeCurtidas == 1) curtidas = "Uma curtida";
  else if (qtdeCurtidas > 1) curtidas = `${qtdeCurtidas} curtidas`;

  // TODO: Criar progresso de entrega
  let progress: number | undefined = undefined;
  if (
    item.permiteResposta &&
    ((item.perguntasFormatadas ?? []).length > 0 || item.respostaComAnexo)
  ) {
    progress = undefined; // Math.floor(Math.random() * (100 - 0 + 1) + 0);
  }

  const unreads = messages
    .filter((f) => f.idMensagem == item.id)
    .reduce((num, cur) => num + (cur.chats?.length || 0), 0);

  return (
    <Fragment>
      <TableRow
        sx={{ "& > td:not([rowSpan])": { borderBottom: "unset !important" } }}
      >
        {!isMd && (
          <TableCell>
            <Typography variant="caption">
              {item.mensagemCategoria?.nome}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          <Tooltip
            title={format(
              parseISO((item.createdAt ?? "") as string),
              "dd/MM/yyyy H:mm:ss"
            )}
          >
            <Typography variant="caption">
              {formatDistance(
                parseISO((item.createdAt ?? "") as string),
                new Date(),
                { locale: ptBR, addSuffix: true }
              )}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <RowEnviadaAtribuicao atribuicoes={item.mensagemAtribuicoes ?? []} />
        </TableCell>
        <TableCell rowSpan={2}>
          {item.rascunho && (
            <Typography variant="caption" component="p">
              Rascunho
            </Typography>
          )}
          {!item.rascunho &&
            item.enviarEm &&
            differenceInMinutes(
              parseISO(item.enviarEm.toString()),
              new Date()
            ) > 0 && (
              <Tooltip
                title={format(
                  parseISO((item.enviarEm.toString() ?? "") as string),
                  "dd/MM/yyyy"
                )}
              >
                <Typography variant="caption" component="p">
                  {formatDistance(
                    parseISO(item.enviarEm.toString()),
                    new Date(),
                    {
                      addSuffix: true,
                      locale: ptBR,
                    }
                  )}
                </Typography>
              </Tooltip>
            )}
          {!item.rascunho &&
            (!item.enviarEm ||
              differenceInMinutes(
                parseISO(item.enviarEm.toString()),
                new Date()
              ) <= 0) && (
              <Typography variant="caption" component="p">
                Sim
              </Typography>
            )}
          {progress && (
            <LinearProgress variant="determinate" value={progress} />
          )}
        </TableCell>
        <TableCell sx={{ textAlign: "right" }} rowSpan={2}>
          {!isSm && (
            <>
              <Tooltip title={curtidas}>
                <span>
                  <IconButton
                    disabled={qtdeCurtidas == 0}
                    color={"error"}
                    disableRipple
                    sx={{ cursor: "default" }}
                  >
                    <Badge badgeContent={qtdeCurtidas}>
                      <CurtidaIcon />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={!item.rascunho ? "Publicado" : "Publicar"}>
                <IconButton
                  size="small"
                  color={!item.rascunho ? "primary" : "secondary"}
                  onClick={() => {
                    if (!item.id) {
                      snack.error("Erro ao recuperar o ID da publicação");
                      return;
                    }
                    handlePublicar(item.id, !item.rascunho);
                  }}
                >
                  <PublicarIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Detalhes">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!item.id) return;

                    handleDetalhes(item.id);
                  }}
                >
                  <Badge badgeContent={unreads} color="secondary">
                    <DetailIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleClick} size="small">
                <MoreIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={() => {
                    if (!item.id) {
                      snack.error("Erro ao recuperar o ID da publicação");
                      return;
                    }
                    if (!item.mensagemCategoria) {
                      snack.error(
                        "Erro ao recuperar a categoria da publicação"
                      );
                      return;
                    }
                    handleClose();
                    handleEdit({
                      categoria: {
                        ...item.mensagemCategoria,
                        id: item.idMensagemCategoria,
                      },
                      idMensagem: item.id,
                    });
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (!item.id) {
                      snack.error("Erro ao recuperar o ID da publicação");
                      return;
                    }

                    handleClose();
                    handleDelete(item.id);
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Excluir</ListItemText>
                </MenuItem>
                {escola.codigo == "0605" && (
                  <MenuItem
                    onClick={() => {
                      if (!item.id) {
                        snack.error("Erro ao recuperar o ID da publicação");
                        return;
                      }
                      handleClose();
                      const url = `https://deltasge.net/novidade/${utils.base64Encode(
                        item.id.toString()
                      )}`;
                      navigator.clipboard.writeText(url);
                      snack.success("Link copiado com sucesso!");
                    }}
                  >
                    <ListItemIcon>
                      <ContentCopy fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Copiar Link</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
          {isSm && (
            <>
              <IconButton onClick={handleClick}>
                <MoreIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={() => {
                    if (!item.id) {
                      snack.error("Erro ao recuperar o ID da publicação");
                      return;
                    }
                    handleClose();
                    handlePublicar(item.id, !item.rascunho);
                  }}
                >
                  <ListItemIcon>
                    <PublicarIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {!item.rascunho ? "Publicado" : "Publicar"}
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (!item.id) {
                      snack.error("Erro ao recuperar o ID da publicação");
                      return;
                    }
                    handleClose();
                    handleDetalhes(item.id);
                  }}
                >
                  <ListItemIcon>
                    <DetailIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Detalhes</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (!item.id) return;
                    if (!item.mensagemCategoria) return;
                    handleClose();
                    handleEdit({
                      categoria: {
                        ...item.mensagemCategoria,
                        id: item.idMensagemCategoria,
                      },
                      idMensagem: item.id,
                    });
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (!item.id) {
                      snack.error("Erro ao recuperar o ID da publicação");
                      return;
                    }
                    handleClose();
                    handleDelete(item.id);
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Excluir</ListItemText>
                </MenuItem>
                {escola.codigo == "0605" && (
                  <MenuItem
                    onClick={() => {
                      if (!item.id) {
                        snack.error("Erro ao recuperar o ID da publicação");
                        return;
                      }
                      handleClose();
                      const url = `https://deltasge.net/novidade/${utils.base64Encode(
                        item.id.toString()
                      )}`;
                      navigator.clipboard.writeText(url);
                      snack.success("Link copiado com sucesso!");
                    }}
                  >
                    <ListItemIcon>
                      <ContentCopy fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Copiar Link</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={!isMd ? 5 : 4}
        >
          <Typography variant="body1">{title}</Typography>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
